/**
 * Admin V3 — Financial / Payouts — Search / filter bar
 *
 * Layout: [Status filter] [Driver ID input] [Search input] [Result counter]
 *
 * Status filter: 8 union values (PayoutState canonical 6 + PayoutStatus legacy 4).
 * Driver ID input: server-side via ?driver_id=
 * Search input: client-side over loaded page (payout_id, booking_id, driver_id).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { payoutStatusMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

const ALL_SENTINEL = '__all__';

// Filter dropdown values — DEDUPLICATED user-facing taxonomy.
//
// The display dictionary `payoutStatusMeta` retains BOTH `paid` (canonical
// PayoutState) and `paid_out` (legacy PayoutStatus) for defensive rendering —
// any record on disk with either value still displays the same "Versé"
// emerald pill. But the filter dropdown shows "Versé" ONCE, mapped to the
// canonical `paid` value. Legacy `paid_out` records will not match this
// filter; this is acceptable because PayoutState is the current taxonomy and
// new records use `paid`.
const STATUS_VALUES = [
  'created', 'pending', 'released', 'paid',
  'failed', 'reversed', 'cancelled',
];

const FinancialPayoutsSearchBar = ({
  status,
  onStatusChange,
  driverId,
  onDriverIdChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const selectValue = status || ALL_SENTINEL;
  const handleStatus = (v) => onStatusChange(v === ALL_SENTINEL ? '' : v);

  const resultsCountText = t('financial.payouts.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Select value={selectValue} onValueChange={handleStatus}>
        <SelectTrigger
          className="h-10 w-[220px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-financial-payouts-status-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('financial.payouts.filter.statusAll', locale)}</SelectItem>
          {STATUS_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{payoutStatusMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <input
        type="text"
        value={driverId}
        onChange={(e) => onDriverIdChange(e.target.value)}
        placeholder={t('financial.payouts.driverPlaceholder', locale)}
        data-testid="adminv3-financial-payouts-driver-input"
        className="h-10 w-[260px] rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
      />

      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('financial.payouts.search.placeholder', locale)}
          data-testid="adminv3-financial-payouts-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-financial-payouts-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-financial-payouts-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default FinancialPayoutsSearchBar;
